// payments.view — Status Pembayaran: rekonsiliasi + aging/tempo + edit tagihan + histori.
import { rp, rpFmt, rpVal, esc, ageDays, todayISO, fillTpl, pecahUnit, tglID } from '@domain/format';
import { currentUser } from '@core/session';
import { canMarkLunas, canVerify, canSeeModal, canEditBayar } from '@domain/access';
import { omzetTiket, piutangTiket, billedNet, isPiutangCara, wajibJatuhTempo } from '@domain/money';
import { caraBayarOpsi } from '@core/akunkas.cache';
import { cabangOpsi, opsiCabangSyncHTML } from '@core/cabangOpsi';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { kirimPengingat } from '@modules/penagihan/penagihan.repo';
import { refreshCounts } from '@core/nav';
import { openTicketHistory } from '@core/history';
import { waTpl, kebijakan } from '@core/settings';
import { financeSlaInfo, financePendingStartMs } from '@domain/sla';
import { finSlaBadge, penagihanBadge } from '@ui/sla-badge';
import { downloadXLS } from '@ui/xls';
import type { Ticket, Role } from '@core/types';
import * as repo from './payments.repo';
import * as svc from './payments.service';
import { verifyRefund } from '@modules/refund/refund.service';

type PayItem = Awaited<ReturnType<typeof repo.listPayable>>[number];
const FILTERS: [string, string][] = [
  ['all', 'Semua'], ['belum', 'Belum lunas'], ['lunas_unverif', 'Lunas · belum verif'],
  ['lunas_verif', 'Terverifikasi'], ['tempo', 'Tempo'], ['refund', 'Refund'],
];
let fStatus = 'all', fQ = '', fBulan = '', fTahun = '', fTeknisi = '', fCabang = '', fOverdueOnly = false;

/** Pra-pilih filter status sebelum membuka Status Pembayaran (mis. drill dari "Belum ditempel ke akun").
 *  Diamankan ke nilai yang dikenal supaya tak menyetel filter yang tak ada. */
export function setPayFilter(status: string): void { if (FILTERS.some(([k]) => k === status)) fStatus = status; }
const BULAN = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const NAMA_BLN = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

const isOverdue = (t: Ticket): boolean => !!t.jatuhTempo && !t.lunas && t.jatuhTempo < todayISO();
// Waktu-mulai fase menunggu (ms) untuk badge SLA — diisi di renderPayments dari log tiket pending (scoped fetch).
let slaVerifyStart = new Map<string, number>(), slaRefundStart = new Map<string, number>();
const matchFilter = (t: Ticket): boolean => {
  switch (fStatus) {
    case 'belum': return !t.refund && !t.lunas && piutangTiket(t) > 0;
    case 'lunas_unverif': return !t.refund && !!t.lunas && !t.verified;
    case 'lunas_verif': return !t.refund && !!t.verified;
    case 'tempo': return isPiutangCara(t.caraBayar);
    case 'refund': return !!t.refund;
    default: return true;
  }
};

export async function renderPayments(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="ttl">💳 Status Pembayaran</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let items: PayItem[];
  await cabangOpsi();          // isi ingatan daftar cabang sebelum filter digambar
  try { items = await repo.listPayable(); }
  catch (e) { root.innerHTML = `<div class="ttl">💳 Status Pembayaran</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }
  // Badge SLA verifikasi bayar/refund butuh waktu-mulai dari ticket_logs — fetch HANYA baris pending (scoped), saat SLA aktif.
  slaVerifyStart = new Map(); slaRefundStart = new Map();
  if (kebijakan().slaOn) {
    const pv = items.filter((i) => !i.ticket.refund && i.ticket.lunas && !i.ticket.verified);
    const prf = items.filter((i) => !!i.ticket.refund && i.ticket.refund.status !== 'verified');
    const nos = [...pv, ...prf].map((i) => i.ticket.no);
    if (nos.length) {
      try {
        const logs = await repo.fetchLogsFor(nos);
        pv.forEach((i) => { const s = financePendingStartMs(logs.get(i.ticket.no) ?? [], 'verify'); if (!isNaN(s)) slaVerifyStart.set(i.ticket.no, s); });
        prf.forEach((i) => { const s = financePendingStartMs(logs.get(i.ticket.no) ?? [], 'refund'); if (!isNaN(s)) slaRefundStart.set(i.ticket.no, s); });
      } catch { /* badge absen bila gagal — tak gagalkan render */ }
    }
  }
  paint(root, items);
}

// paint = render dari data yg SUDAH di-fetch. Filter = klien murni → TANPA refetch (tak ada flash "Memuat",
// angka ringkasan stabil dalam 1 sesi). Aksi MUTASI (lunas/verify/refund/edit) → hardRefresh yg refetch.
function paint(root: HTMLElement, items: PayItem[]): void {
  const u = currentUser()!;
  const hardRefresh = (): void => { void renderPayments(root); refreshCounts(); }; // refreshCounts → badge 💬 sidebar auto-turun pasca validasi
  const repaint = (): void => paint(root, items);

  // Ringkasan & aging dari SELURUH piutang in-scope (stabil, tak ikut filter tabel).
  const piutangItems = items.filter((i) => piutangTiket(i.ticket) > 0);
  const totalPiutang = piutangItems.reduce((a, i) => a + piutangTiket(i.ticket), 0);
  const totalOmzet = items.reduce((a, i) => a + omzetTiket(i.ticket), 0);
  const overdue = piutangItems.filter((i) => isOverdue(i.ticket));
  const overdueRp = overdue.reduce((a, i) => a + piutangTiket(i.ticket), 0);
  // Aging buckets — batas hari settable di Pengaturan → SLA & Waktu (konsisten dgn Laporan Piutang).
  const kk = kebijakan(), agB1 = kk.agingB1Hari, agB2 = kk.agingB2Hari, agB3 = kk.agingB3Hari;
  const AGE_LBL = { a: `0–${agB1} hari`, b: `${agB1 + 1}–${agB2}`, c: `${agB2 + 1}–${agB3}`, d: `> ${agB3}` };
  const bucket = { a: 0, b: 0, c: 0, d: 0 };
  piutangItems.forEach((i) => {
    const age = ageDays(i.ticket.tglAmbilISO);
    const v = piutangTiket(i.ticket);
    if (age <= agB1) bucket.a += v; else if (age <= agB2) bucket.b += v; else if (age <= agB3) bucket.c += v; else bucket.d += v;
  });

  // Daftar teknisi & tahun (dari tgl ambil) untuk dropdown filter.
  const teknisiList = [...new Set(items.map((i) => i.ticket.teknisi).filter((t): t is string => !!t))].sort();
  const tahunList = [...new Set(items.map((i) => (i.ticket.tglAmbilISO ?? '').slice(0, 4)).filter(Boolean))].sort().reverse();
  // Clamp filter stale: bila opsi tersimpan tak ada lagi di data → reset (cegah filter tersembunyi = tabel kosong tanpa sebab).
  if (fTeknisi && !teknisiList.includes(fTeknisi)) fTeknisi = '';
  const opsiCab = opsiCabangSyncHTML(fCabang);
  if (fTahun && !tahunList.includes(fTahun)) fTahun = '';
  const q = fQ.trim().toLowerCase();
  const matchExtra = (i: PayItem): boolean => {
    const t = i.ticket;
    if (q && !(`${t.no} ${i.custNama}`.toLowerCase().includes(q))) return false;
    const ym = (t.tglAmbilISO ?? '').slice(0, 7); // YYYY-MM
    if (fTahun && (t.tglAmbilISO ?? '').slice(0, 4) !== fTahun) return false;
    if (fBulan && ym.slice(5, 7) !== fBulan) return false;
    if (fTeknisi && t.teknisi !== fTeknisi) return false;
    if (fCabang && t.outlet !== fCabang) return false;
    if (fOverdueOnly && !(isOverdue(t) && piutangTiket(t) > 0)) return false;
    return true;
  };
  const shown = items.filter((i) => matchFilter(i.ticket) && matchExtra(i));
  const shownPiutang = shown.reduce((a, i) => a + piutangTiket(i.ticket), 0);
  const rowsHtml = shown.map(({ ticket: t, custNama }) => renderRow(t, custNama, u.role)).join('')
    || `<tr><td colspan="10" class="empty">Tidak ada transaksi untuk filter ini.</td></tr>`;

  const stat = (lbl: string, val: string, color: string): string =>
    `<div class="stat"><div class="lbl">${lbl}</div><div class="num" style="color:${color}">${val}</div></div>`;
  const agingCell = (lbl: string, v: number): string =>
    `<div style="flex:1;min-width:96px;border:1px solid var(--line);border-radius:8px;padding:6px 10px"><div style="font-size:11px;color:var(--muted)">${lbl}</div><div style="font-weight:700">${rp(v)}</div></div>`;

  root.innerHTML = `<div class="ttl">💳 Status Pembayaran</div>
    <div class="scope-note">Rekonsiliasi 2 langkah: CS tandai <b>Lunas</b> → Finance <b>verifikasi</b>. Omzet diakui setelah verifikasi.</div>
    <div class="stat-grid" style="margin-bottom:12px">
      ${stat('Total Piutang', rp(totalPiutang), 'var(--danger)')}
      ${overdue.length ? stat('⚠️ Jatuh tempo lewat', `${overdue.length} · ${rp(overdueRp)}`, 'var(--danger)') : ''}
      ${canSeeModal(u.role) ? stat('Omzet diakui', rp(totalOmzet), 'var(--ok)') : ''}
    </div>
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px">
      ${agingCell(AGE_LBL.a, bucket.a)}${agingCell(AGE_LBL.b, bucket.b)}${agingCell(AGE_LBL.c, bucket.c)}${agingCell(AGE_LBL.d, bucket.d)}
    </div>
    <div class="toolbar" style="display:flex;gap:8px;align-items:center;margin-bottom:12px;flex-wrap:wrap">
      <input id="pf_q" placeholder="🔍 No / pelanggan" value="${esc(fQ)}" style="max-width:150px">
      <select id="pf_status">${FILTERS.map(([k, l]) => `<option value="${k}" ${fStatus === k ? 'selected' : ''}>${l}</option>`).join('')}</select>
      <select id="pf_bulan"><option value="">Semua bln</option>${BULAN.map((b, idx) => `<option value="${b}" ${fBulan === b ? 'selected' : ''}>${NAMA_BLN[idx]}</option>`).join('')}</select>
      <select id="pf_tahun"><option value="">Semua thn</option>${tahunList.map((y) => `<option value="${esc(y)}" ${fTahun === y ? 'selected' : ''}>${esc(y)}</option>`).join('')}</select>
      <select id="pf_cab">${opsiCab}</select>
      <select id="pf_tek"><option value="">Semua teknisi</option>${teknisiList.map((t) => `<option value="${esc(t)}" ${fTeknisi === t ? 'selected' : ''}>${esc(t)}</option>`).join('')}</select>
      <label style="font-size:13px;display:flex;align-items:center;gap:4px"><input type="checkbox" id="pf_overdue" ${fOverdueOnly ? 'checked' : ''}> ⚠️ Jatuh tempo lewat</label>
      <button class="btn" id="pf_reset" style="padding:5px 10px" title="Bersihkan filter" aria-label="Bersihkan filter">↺</button>
      <span style="font-size:12px;color:var(--muted)">${shown.length} tiket</span>
      <button class="btn" id="pf_export" style="margin-left:auto">⬇️ Excel</button>
    </div>
    <table class="data"><thead><tr><th>No. Servis</th><th>Pelanggan</th><th>Tagihan</th><th>DP</th><th>Piutang</th><th>Cara</th><th>Jatuh Tempo</th><th>Umur</th><th>Status</th><th>Aksi</th></tr></thead><tbody>${rowsHtml}</tbody></table>`;

  // Filter → repaint klien (tanpa refetch). Aksi mutasi → hardRefresh (refetch, di bawah).
  (root.querySelector('#pf_status') as HTMLSelectElement).addEventListener('change', (e) => {
    fStatus = (e.target as HTMLSelectElement).value; repaint();
  });
  const selVal = (id: string, set: (v: string) => void): void => {
    const el = root.querySelector<HTMLSelectElement>('#' + id); if (el) el.onchange = (): void => { set(el.value); repaint(); };
  };
  selVal('pf_bulan', (v) => { fBulan = v; });
  selVal('pf_tahun', (v) => { fTahun = v; });
  selVal('pf_tek', (v) => { fTeknisi = v; });
  selVal('pf_cab', (v) => { fCabang = v; });
  const ov = root.querySelector<HTMLInputElement>('#pf_overdue'); if (ov) ov.onchange = (): void => { fOverdueOnly = ov.checked; repaint(); };
  const qi = root.querySelector<HTMLInputElement>('#pf_q');
  // Live filter tiap ketik (client-only → cepat). repaint membangun ulang input → kembalikan fokus + kursor ke akhir.
  if (qi) qi.oninput = (): void => {
    fQ = qi.value; repaint();
    const nq = root.querySelector<HTMLInputElement>('#pf_q');
    if (nq) { nq.focus(); nq.setSelectionRange(nq.value.length, nq.value.length); }
  };
  root.querySelector('#pf_reset')?.addEventListener('click', () => { fQ = ''; fBulan = ''; fTahun = ''; fTeknisi = ''; fCabang = ''; fOverdueOnly = false; fStatus = 'all'; repaint(); });
  const byNoItem = new Map(items.map((i) => [i.ticket.no, i]));
  const byNo = new Map(items.map((i) => [i.ticket.no, i.ticket]));
  // markLunas/verify pakai dialog konfirmasi (aksi finansial tak boleh 1-klik tanpa sadar).
  root.querySelectorAll<HTMLElement>('button[data-lunas]').forEach((b) => b.addEventListener('click', () => {
    const no = b.dataset.lunas ?? ''; const it = byNoItem.get(no);
    lunasDialog(no, it ? piutangTiket(it.ticket) : 0, (via) => act(() => svc.markLunas(no, via), '✅ Ditandai lunas', hardRefresh));
  }));
  root.querySelectorAll<HTMLElement>('button[data-verify]').forEach((b) => b.addEventListener('click', () => {
    const no = b.dataset.verify ?? '';
    confirmDialog(`Verifikasi pembayaran <b>${esc(no)}</b>?<br><span style="color:var(--muted)">Omzet diakui setelah verifikasi. Aksi ini tercatat atas nama Anda.</span>`,
      () => act(() => svc.verifyBayar(no), '✅ Terverifikasi', hardRefresh));
  }));
  root.querySelectorAll<HTMLElement>('button[data-refverify]').forEach((b) => b.addEventListener('click', () => {
    const no = b.dataset.refverify ?? '';
    confirmDialog(`Verifikasi <b>refund</b> ${esc(no)}?<br><span style="color:var(--muted)">Dana dikembalikan ke pelanggan; omzet berkurang.</span>`,
      () => act(() => verifyRefund(no), '✅ Refund terverifikasi', hardRefresh));
  }));
  root.querySelectorAll<HTMLElement>('button[data-hist]').forEach((b) => b.addEventListener('click', () => void openTicketHistory(b.dataset.hist ?? '')));
  root.querySelectorAll<HTMLElement>('button[data-edit]').forEach((b) => b.addEventListener('click', () => {
    const t = byNo.get(b.dataset.edit ?? ''); if (t) editBillingDialog(t, hardRefresh);
  }));
  // 💬 Tagih — WA penagihan piutang (dummy-safe di dev via openWA).
  root.querySelectorAll<HTMLElement>('button[data-tagih]').forEach((b) => b.addEventListener('click', () => {
    const it = byNoItem.get(b.dataset.tagih ?? ''); if (!it) return;
    const piut = piutangTiket(it.ticket);
    const jt = it.ticket.jatuhTempo ? ` (jatuh tempo ${tglID(it.ticket.jatuhTempo)})` : '';
    // Lewat kirimPengingat, BUKAN openWA langsung: tiap kiriman harus meninggalkan jejak, kalau
    // tidak jeda "jangan tagih dua kali" di menu Penagihan jadi bohong.
    kirimPengingat(it.ticket.no, it.custWa, fillTpl(waTpl().tagih, { nama: it.custNama, no: it.ticket.no, unit: it.unit, ...pecahUnit(it.unit), sisa: rp(piut), jt }), 'Tagih piutang', toast);
  }));
  // ⬇️ Export Excel (baris sesuai filter aktif).
  (root.querySelector('#pf_export') as HTMLButtonElement | null)?.addEventListener('click', () => {
    const head = '<tr><th>No. Servis</th><th>Pelanggan</th><th>Tagihan</th><th>DP</th><th>Piutang</th><th>Cara</th><th>Jatuh Tempo</th><th>Umur</th><th>Status</th></tr>';
    const body = shown.map(({ ticket: t, custNama }) => {
      const st = t.refund ? (t.refund.status === 'verified' ? 'Refund ✔' : 'Refund ⏳')
        : (t.lunas ? (t.verified ? 'Lunas ✔' : 'Lunas · belum verif') : (isOverdue(t) && piutangTiket(t) > 0 ? 'Jatuh Tempo' : (isPiutangCara(t.caraBayar) ? 'Tempo/Piutang' : 'Belum')));
      const umur = t.tglAmbilISO ? `${ageDays(t.tglAmbilISO)} hr` : '—';
      return `<tr${t.refund ? ' style="color:#c0392b"' : ''}><td>${esc(t.no)}</td><td>${esc(custNama)}</td><td class="r">${billedNet(t)}</td><td class="r">${t.dp ?? 0}</td><td class="r">${piutangTiket(t)}</td><td>${esc(t.caraBayar ?? '')}</td><td>${esc(t.jatuhTempo ?? '')}</td><td>${esc(umur)}</td><td>${esc(st)}</td></tr>`;
    }).join('');
    const ctx = [`Status: ${FILTERS.find(([k]) => k === fStatus)?.[1] ?? 'Semua'}`,
      fTeknisi ? `Teknisi: ${fTeknisi}` : '', fBulan ? `Bln: ${fBulan}` : '', fTahun ? `Thn: ${fTahun}` : '',
      fOverdueOnly ? 'Jatuh tempo lewat' : '', q ? `Cari: ${fQ}` : ''].filter(Boolean).map(esc).join(' · ');
    const meta = `<h2>Status Pembayaran — iFiCs</h2><p>${ctx}<br>${shown.length} tiket ditampilkan · Piutang subset ${rp(shownPiutang)} · (Total piutang in-scope ${rp(totalPiutang)})</p>`;
    downloadXLS(`status-pembayaran-${todayISO()}.xls`, `${meta}<table>${head}${body}</table>`);
  });
}

/**
 * Pelunasan WAJIB menyebut metode uang diterima. Untuk tempo, `cara_bayar` berisi "Tempo" —
 * itu bukan cara menerima uang; tanpa metode, uangnya tak bisa dicocokkan ke laci atau rekening
 * saat Tutup Kasir. Default Tunai karena itu yang paling sering di kasir toko.
 */
function lunasDialog(no: string, piutang: number, onOk: (via: string) => void): void {
  const opsi = ['Tunai', 'Transfer', 'QRIS', 'Lainnya'];
  const bg = openModal(`<div class="modal-head"><h3>Tandai LUNAS — ${esc(no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <p style="margin:0 0 10px">Piutang <b>${rp(piutang)}</b> akan dianggap dibayar (menunggu verifikasi Finance).</p>
      <div class="field"><label>Uang diterima via <span class="req">*</span></label>
        <select id="lz_via">${opsi.map((o) => `<option${o === 'Tunai' ? ' selected' : ''}>${o}</option>`).join('')}</select>
        <div class="scope-note">Dipakai Tutup Kasir untuk memisahkan uang laci dan uang bank.</div></div>
    </div>
    <div class="modal-foot"><button class="btn" id="lz_no">Batal</button><button class="btn primary" id="lz_yes">Ya, tandai lunas</button></div>`);
  bg.querySelector('#lz_no')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#lz_yes')?.addEventListener('click', () => {
    const via = (bg.querySelector('#lz_via') as HTMLSelectElement | null)?.value ?? 'Tunai';
    bg.remove(); onOk(via);
  });
}

function confirmDialog(html: string, onOk: () => void): void {
  const bg = openModal(`<div class="modal-head"><h3>Konfirmasi</h3><button class="x">×</button></div>
    <div class="modal-body"><p style="margin:0">${html}</p></div>
    <div class="modal-foot"><button class="btn" id="cf_no">Batal</button><button class="btn primary" id="cf_yes">Ya, lanjut</button></div>`);
  bg.querySelector('#cf_no')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#cf_yes')?.addEventListener('click', () => { bg.remove(); onOk(); });
}

function renderRow(t: Ticket, custNama: string, role: Role): string {
  const piut = piutangTiket(t);
  const age = t.tglAmbilISO ? `${ageDays(t.tglAmbilISO)} hr` : '—';
  const tempo = t.jatuhTempo ? `<span style="color:${isOverdue(t) ? 'var(--danger)' : 'var(--txt)'}">${esc(t.jatuhTempo)}${isOverdue(t) ? ' ⚠️' : ''}</span>` : '—';
  const histBtn = `<button class="btn" style="padding:4px 8px" data-hist="${esc(t.no)}" title="Histori" aria-label="Histori transaksi ${esc(t.no)}">📜</button>`;
  const editBtn = canEditBayar(role) ? `<button class="btn" style="padding:4px 8px" data-edit="${esc(t.no)}" title="Edit tagihan" aria-label="Edit tagihan ${esc(t.no)}">✏️</button>` : '';
  const tagihBtn = (piut > 0 && !t.refund && role !== 'Teknisi') ? `<button class="btn" style="padding:4px 8px" data-tagih="${esc(t.no)}" title="Tagih via WA" aria-label="Tagih via WA ${esc(t.no)}">💬</button>` : '';
  let status: string, color: string, act = '';
  if (t.refund) {
    const v = t.refund.status === 'verified';
    status = `💸 Refund ${v ? '✔' : '⏳'}`; color = 'var(--danger)';
    act = !v && canVerify(role) ? `<button class="btn primary" data-refverify="${esc(t.no)}">Verifikasi Refund</button>` : '';
  } else {
    const over = isOverdue(t) && piut > 0;
    status = t.lunas ? (t.verified ? 'Lunas ✔' : 'Lunas · belum verif') : (over ? 'Jatuh Tempo' : (isPiutangCara(t.caraBayar) ? 'Tempo/Piutang' : 'Belum'));
    color = t.verified ? 'var(--ok)' : (t.lunas ? 'var(--warn)' : (over ? 'var(--danger)' : (isPiutangCara(t.caraBayar) ? 'var(--warn)' : 'var(--danger)')));
    const l = !t.lunas && canMarkLunas(role) ? `<button class="btn" data-lunas="${esc(t.no)}">Lunasi</button>` : '';
    const vf = t.lunas && !t.verified && canVerify(role) ? `<button class="btn primary" data-verify="${esc(t.no)}">Verifikasi</button>` : '';
    act = `${l} ${vf}`;
  }
  // Badge SLA finance (read-only): verifikasi bayar/refund (dari waktu-mulai log) + penagihan (lewat tempo vs grace eskalasi).
  const k = kebijakan(), min = k.slaOn;
  let slaB = '';
  if (t.refund && t.refund.status !== 'verified') slaB = finSlaBadge(financeSlaInfo(slaRefundStart.get(t.no) ?? NaN, null, min ? k.slaVerifikasiRefundMenit : 0));
  else if (!t.refund && t.lunas && !t.verified) slaB = finSlaBadge(financeSlaInfo(slaVerifyStart.get(t.no) ?? NaN, null, min ? k.slaVerifikasiBayarMenit : 0));
  else if (!t.refund && !t.lunas && isOverdue(t) && piut > 0) slaB = penagihanBadge(t.jatuhTempo, todayISO(), k.slaPenagihanEskalasiHari, k.slaOn);
  return `<tr><td><b>${esc(t.no)}</b></td><td>${esc(custNama)}</td><td class="r">${rp(billedNet(t))}</td><td class="r">${rp(t.dp ?? 0)}</td><td class="r">${rp(piut)}</td><td>${esc(t.caraBayar ?? '—')}</td><td>${tempo}</td><td>${age}</td><td><span style="color:${color};font-weight:600">${esc(status)}</span>${slaB}</td><td style="white-space:nowrap">${act} ${tagihBtn}${histBtn}${editBtn}</td></tr>`;
}

async function act(fn: () => Promise<void>, ok: string, refresh: () => void): Promise<void> {
  try { await fn(); toast(ok); refresh(); } catch (e) { toast((e as Error).message); }
}

function editBillingDialog(t: Ticket, refresh: () => void): void {
  const bg = openModal(`<div class="modal-head"><h3>Edit Tagihan — ${esc(t.no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Khusus Owner/Manager. Perubahan memengaruhi tagihan & piutang.</div>
      <div class="row2"><div class="field"><label>Biaya (tagihan)</label><input id="eb_biaya" value="${rpFmt(String(t.biaya ?? 0))}" inputmode="numeric"></div>
        <div class="field"><label>DP</label><input id="eb_dp" value="${rpFmt(String(t.dp ?? 0))}" inputmode="numeric"></div></div>
      <div class="row2"><div class="field"><label>Cara bayar</label><select id="eb_cara">${caraBayarOpsi(t.outlet).map((c) => `<option ${t.caraBayar === c ? 'selected' : ''}>${esc(c)}</option>`).join('')}</select></div>
        <div class="field" id="eb_tempobox"><label>Jatuh tempo</label><input id="eb_tempo" type="date" value="${esc(t.jatuhTempo ?? '')}"></div></div>
      <label style="display:flex;gap:6px;align-items:center;font-size:13px;margin-top:6px"><input type="checkbox" id="eb_unset"> Batalkan status lunas (kembali belum lunas)</label>
      <div class="err" id="eb_err"></div>
    </div>
    <div class="modal-foot"><button class="btn" id="eb_cancel">Batal</button><button class="btn primary" id="eb_save">Simpan</button></div>`);
  const mi = (id: string): void => { const el = bg.querySelector('#' + id) as HTMLInputElement; el.addEventListener('input', () => { el.value = rpFmt(el.value); }); };
  mi('eb_biaya'); mi('eb_dp');
  const caraSel = bg.querySelector('#eb_cara') as HTMLSelectElement;
  const tempoBox = bg.querySelector('#eb_tempobox') as HTMLElement;
  const syncTempo = (): void => { tempoBox.style.display = isPiutangCara(caraSel.value) ? '' : 'none'; };
  caraSel.addEventListener('change', syncTempo); syncTempo();
  bg.querySelector('#eb_cancel')?.addEventListener('click', () => bg.remove());
  const save = bg.querySelector('#eb_save') as HTMLButtonElement;
  save.addEventListener('click', async () => {
    const cara = caraSel.value;
    const tempoVal = (bg.querySelector('#eb_tempo') as HTMLInputElement).value || null;
    const err = (m: string): void => { const el = bg.querySelector('#eb_err'); if (el) el.textContent = m; };
    if (wajibJatuhTempo(cara) && !tempoVal) { err('Isi tanggal jatuh tempo untuk Tempo (Toko).'); return; }
    save.disabled = true;
    try {
      await svc.editBilling(t.no, {
        biaya: rpVal((bg.querySelector('#eb_biaya') as HTMLInputElement).value),
        dp: rpVal((bg.querySelector('#eb_dp') as HTMLInputElement).value),
        cara, jatuhTempo: isPiutangCara(cara) ? tempoVal : null,
        unsettle: (bg.querySelector('#eb_unset') as HTMLInputElement).checked,
      });
      toast('✅ Tagihan diperbarui'); bg.remove(); refresh();
    } catch (e) { err((e as Error).message); save.disabled = false; }
  });
}

